// Components==============
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faCamera } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// @ts-ignore
import Plyr from 'faqta-react-plyr';
import { motion } from 'framer-motion';
import { useMeasure } from 'hooks/useMeasure';
import { useMediaQ } from 'hooks/useMediaQ';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { useRootModal } from 'hooks/useRootModal';
import React, { useEffect, useRef, useState } from 'react';
import { framerFade } from 'utilities/FramerAnimations';
import { getSmartImage } from 'utilities/getSmartImage';
import { getVideoData } from 'utilities/getVideoData';
import ImageSelector from '../ImageSelector/ImageSelector';
import Shade from '../Shade/Shade';
import './Modal.module.scss';
// =========================

type Props = {
  children: React.ReactNode;
  images?: string[];
  image?: { image?: string; path?: string };
  video?: string;
  size?: 'small' | 'small-medium' | 'medium' | 'large';
  disablePositioning?: boolean;
  externalRef?: React.RefObject<HTMLDivElement>;
  noPadding?: boolean;
  noCross?: boolean;
  collapsed?: boolean;
  sideModalContent?: React.ReactNode;
  topIcon?: { image: string; name: string };
  setModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  closeFunction?: () => void;
  handleSubmitImage?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  preventClose?: boolean;
};

export default function Modal({
  setModalIsOpen,
  images,
  image,
  video,
  children,
  size = 'large',
  disablePositioning,
  externalRef,
  noPadding,
  noCross,
  collapsed,
  sideModalContent,
  topIcon,
  closeFunction,
  handleSubmitImage,
  preventClose,
}: Props) {
  const { closeModal, rootModal } = useRootModal();
  const [currentImage, setCurrentImage] = useState(images?.[0] || video || '');
  const ref = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sideModalRef = useRef<HTMLDivElement>(null);
  const modalRef = disablePositioning ? externalRef : ref;
  const { height } = useMeasure(modalRef);

  const handleClose = () =>
    closeFunction
      ? closeFunction()
      : rootModal
      ? closeModal()
      : setModalIsOpen
      ? setModalIsOpen(false)
      : () => {};

  useOnClickOutside({
    refs: [ref, sideModalRef],
    handler: handleClose,
    condition: preventClose
      ? false
      : rootModal
      ? !setModalIsOpen
      : !disablePositioning,
  });

  const [isOverflown, setIsOverflown] = useState(false);
  useEffect(() => {
    if (
      scrollRef.current &&
      scrollRef?.current?.scrollHeight > scrollRef?.current?.clientHeight
    )
      return setIsOverflown(true);

    setIsOverflown(false);
  }, [height]);

  const maxWidth =
    size === 'small'
      ? 300
      : size === 'medium'
      ? 750
      : size === 'large'
      ? 1000
      : 375;

  const query = useMediaQ('min', 1325);

  const { videoType, videoId, videoControls } = getVideoData(video);

  return (
    <>
      <div
        styleName={`${
          disablePositioning ? 'no-position' : ''
        } scroll-container ${!setModalIsOpen ? 'root-modal' : ''} ${
          collapsed ? 'collapsed' : ''
        } ${topIcon && isOverflown ? 'with-top-icon' : ''}`}
        ref={scrollRef}
      >
        <motion.div
          styleName={`container ${sideModalContent ? 'with-side-modal' : ''}`}
          style={{
            top: isOverflown ? 0 : '50%',
            transform: isOverflown
              ? 'translate(-50%, 0)'
              : 'translate(-50%, -50%)',
          }}
          {...framerFade}
        >
          <div
            styleName={`main-wrapper  ${images || video ? 'with-images' : ''}`}
            style={
              sideModalContent
                ? !query
                  ? { maxWidth }
                  : { width: maxWidth }
                : { maxWidth }
            }
            ref={modalRef}
          >
            {!!topIcon && (
              <div styleName="top-icon">
                <img src={topIcon.image} alt={topIcon.name as string} />
                <p>{topIcon.name}</p>
              </div>
            )}
            {!noCross && (
              <div styleName="close-button" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
              </div>
            )}
            {(images || video) && (
              <div styleName="image-wrapper">
                <div styleName="image" />
                {currentImage.includes('https') ? (
                  <div styleName="video">
                    <Plyr
                      type={videoType}
                      controls={videoControls}
                      videoId={videoId}
                      fullscreen={{
                        enabled: true,
                        fallback: true,
                        iosNative: true,
                      }}
                    />
                  </div>
                ) : (
                  <img
                    src={getSmartImage(currentImage)}
                    alt="background"
                    styleName="image"
                  />
                )}
                {images && (
                  <ImageSelector
                    images={images.map((i) => ({ path: i }))}
                    video={video}
                    currentImage={currentImage}
                    setCurrentImage={setCurrentImage}
                    shouldGoDown={video === currentImage}
                  />
                )}
              </div>
            )}
            {!!(image || handleSubmitImage) && (
              <div styleName="single-image">
                {!!image?.image && (
                  <img
                    src={getSmartImage(image.image, image.path)}
                    alt="background"
                  />
                )}
                {!!handleSubmitImage && (
                  <>
                    <input
                      type="file"
                      id="upload"
                      onChange={handleSubmitImage}
                      name="upload"
                    />
                    <label htmlFor="upload">
                      <div styleName={`overlay ${image ? 'with-image' : ''}`} />
                      <FontAwesomeIcon icon={faCamera} />
                    </label>
                  </>
                )}
              </div>
            )}
            <div
              styleName="content-wrapper"
              style={{
                padding: noPadding ? 0 : '',
              }}
            >
              {children}
            </div>
          </div>
          {!!sideModalContent && (
            <div
              styleName="side-wrapper"
              ref={sideModalRef}
              style={{ maxWidth }}
            >
              {sideModalContent}
            </div>
          )}
        </motion.div>
      </div>
      {!disablePositioning && (
        <div styleName={`shade ${!setModalIsOpen ? 'root-modal' : ''}`}>
          <Shade />
        </div>
      )}
    </>
  );
}
